import {
  Address,
  AddressCandidate,
  AddressCandidateTimeZone,
  AddressPropertyType,
  AddressValidationIssue,
  AddressValidationRequest,
  LocationAddress,
  LocationAddressPropertyType,
  LocationAddressTimeZone,
  MonitoringLocationChangeAddress
} from '../../../api/models'
import { IncompleteLocationChange, LocationChangeWithAddress } from '../../../atoms'
import { ADDRESS_ISSUE_MAPPINGS } from '../../../constants'
import { MonitoringLocationChangeAddressCountry, MonitoringLocationChangeAddressTimeZone } from '../../../api/generated/museum-v1-types'

export type IssueMessageForField = {
  fieldName: string
  helperText: string
}

/**
 * Creates a `LocationChange` object based on the provided form data, and optionally an existing location change.
 *
 * @param currentLocationChange _(optional)_ the existing location change to edit
 * @param formData the data from the Address Change form
 */
export function updateCurrentLocationChangeWithUserInput(
  currentLocationChange: IncompleteLocationChange,
  formData: Partial<MonitoringLocationChangeAddress>
): IncompleteLocationChange {
  const result: IncompleteLocationChange = {
    // If editing an existing location change, start with its existing properties
    ...currentLocationChange,

    address: {
      street1: formData.street1!,
      city: formData.city!,
      state: formData.state!,
      postalCode: formData.postalCode!,
      county: formData.county!,
      country: MonitoringLocationChangeAddressCountry.US,
      propertyType: formData.propertyType!,
      timeZone: MonitoringLocationChangeAddressTimeZone.Unknown
    }
  }

  if (formData.timeZone) {
    result.address!.timeZone = formData.timeZone
  }

  if (formData.street2) {
    result.address!.street2 = formData.street2
  }

  if (formData.crossStreet) {
    result.address!.crossStreet = formData.crossStreet
  }

  return result
}

export function toAddressCandidateTimeZone(timeZone: MonitoringLocationChangeAddressTimeZone | undefined) {
  switch (timeZone) {
    case MonitoringLocationChangeAddressTimeZone.America_New_York:
      return AddressCandidateTimeZone.America_New_York
    case MonitoringLocationChangeAddressTimeZone.America_Chicago:
      return AddressCandidateTimeZone.America_Chicago
    case MonitoringLocationChangeAddressTimeZone.America_Denver:
      return AddressCandidateTimeZone.America_Denver
    case MonitoringLocationChangeAddressTimeZone.America_Phoenix:
      return AddressCandidateTimeZone.America_Phoenix
    case MonitoringLocationChangeAddressTimeZone.America_Los_Angeles:
      return AddressCandidateTimeZone.America_Los_Angeles
    case MonitoringLocationChangeAddressTimeZone.America_Anchorage:
      return AddressCandidateTimeZone.America_Anchorage
    case MonitoringLocationChangeAddressTimeZone.Pacific_Honolulu:
      return AddressCandidateTimeZone.Pacific_Honolulu
    case MonitoringLocationChangeAddressTimeZone.Europe_London:
      return AddressCandidateTimeZone.Europe_London
    case MonitoringLocationChangeAddressTimeZone.America_Puerto_Rico:
      return AddressCandidateTimeZone.America_Puerto_Rico
    case MonitoringLocationChangeAddressTimeZone.Pacific_Guam:
      return AddressCandidateTimeZone.Pacific_Guam
    case MonitoringLocationChangeAddressTimeZone.Unknown:
    default:
      return AddressCandidateTimeZone.Unknown
  }
}

export function toAddress(address: MonitoringLocationChangeAddress): Address {
  const result: Address = {
    street1: address.street1 ?? '',
    street2: address.street2 ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zip: address.postalCode ?? '',
    county: address.county ?? '',
    country: address.country ?? '',
    crossStreet: address.crossStreet ?? '',
    timeZone: toAddressCandidateTimeZone(address.timeZone),
    locationOffset: 0
  }

  if (address.street2) {
    result.street2 = address.street2
  }
  if (address.propertyType) {
    result.propertyType = address.propertyType as unknown as AddressPropertyType
  }

  return result
}

export function toAddressValidationRequest(address: Omit<MonitoringLocationChangeAddress, 'timeZone'>): AddressValidationRequest {
  const result: AddressValidationRequest = {
    street1: address.street1,
    city: address.city,
    state: address.state!,
    zip: address.postalCode,
    country: address.country,
    residenceType: address.propertyType as unknown as AddressPropertyType
  }

  if (address.street2) {
    result.street2 = address.street2
  }

  if (address.county) {
    result.county = address.county
  }

  return result
}

export function toMonitoringLocationChangeAddressFromCandidate(
  address: AddressCandidate,
  formData: MonitoringLocationChangeAddress | undefined
): MonitoringLocationChangeAddress {
  const result: MonitoringLocationChangeAddress = {
    propertyType: formData?.propertyType as AddressPropertyType,
    street1: address.street1 ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    county: address.county ?? '',
    postalCode: address.zip ?? '',
    country:
      address.country === MonitoringLocationChangeAddressCountry.GB
        ? MonitoringLocationChangeAddressCountry.GB
        : MonitoringLocationChangeAddressCountry.US,
    timeZone: toMonitoringLocationChangeAddressTimeZone(address.timeZone)
  }

  if (address.street2) {
    result.street2 = address.street2
  }

  if (formData?.crossStreet) {
    result.crossStreet = formData?.crossStreet
  }

  return result
}

export function toAddressPropertyType(propertyType: LocationAddressPropertyType | undefined): AddressPropertyType {
  switch (propertyType) {
    case LocationAddressPropertyType.House:
      return AddressPropertyType.House
    case LocationAddressPropertyType.Apartment:
      return AddressPropertyType.Apartment
    case LocationAddressPropertyType.Commercial:
      return AddressPropertyType.Commercial
    default:
      return AddressPropertyType.House
  }
}

export function toMonitoringLocationChangeAddressFromLocationAddress(address: LocationAddress | undefined): MonitoringLocationChangeAddress {
  const result: MonitoringLocationChangeAddress = {
    propertyType: toAddressPropertyType(address?.propertyType),
    street1: address?.street1 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    county: address?.county ?? '',
    postalCode: address?.zip ?? '',
    country:
      address?.country === MonitoringLocationChangeAddressCountry.GB
        ? MonitoringLocationChangeAddressCountry.GB
        : MonitoringLocationChangeAddressCountry.US,
    timeZone: toMonitoringLocationChangeAddressTimeZoneFromLocationAddressTimeZone(address?.timeZone)
  }

  if (address?.street2) {
    result.street2 = address.street2
  }

  if (address?.crossStreet) {
    result.crossStreet = address.crossStreet
  }

  return result
}

export function toMonitoringLocationChangeAddressTimeZone(timeZone: AddressCandidateTimeZone | undefined): MonitoringLocationChangeAddressTimeZone {
  switch (timeZone) {
    case AddressCandidateTimeZone.America_New_York:
      return MonitoringLocationChangeAddressTimeZone.America_New_York
    case AddressCandidateTimeZone.America_Chicago:
      return MonitoringLocationChangeAddressTimeZone.America_Chicago
    case AddressCandidateTimeZone.America_Denver:
      return MonitoringLocationChangeAddressTimeZone.America_Denver
    case AddressCandidateTimeZone.America_Phoenix:
      return MonitoringLocationChangeAddressTimeZone.America_Phoenix
    case AddressCandidateTimeZone.America_Los_Angeles:
      return MonitoringLocationChangeAddressTimeZone.America_Los_Angeles
    case AddressCandidateTimeZone.America_Anchorage:
      return MonitoringLocationChangeAddressTimeZone.America_Anchorage
    case AddressCandidateTimeZone.Pacific_Honolulu:
      return MonitoringLocationChangeAddressTimeZone.Pacific_Honolulu
    case AddressCandidateTimeZone.Europe_London:
      return MonitoringLocationChangeAddressTimeZone.Europe_London
    case AddressCandidateTimeZone.America_Puerto_Rico:
      return MonitoringLocationChangeAddressTimeZone.America_Puerto_Rico
    case AddressCandidateTimeZone.Pacific_Guam:
      return MonitoringLocationChangeAddressTimeZone.Pacific_Guam
    case AddressCandidateTimeZone.Unresolved:
    case AddressCandidateTimeZone.Unknown:
    default:
      return MonitoringLocationChangeAddressTimeZone.Unknown
  }
}

export function toMonitoringLocationChangeAddressTimeZoneFromLocationAddressTimeZone(
  timeZone: LocationAddressTimeZone | undefined
): MonitoringLocationChangeAddressTimeZone {
  switch (timeZone) {
    case LocationAddressTimeZone.America_New_York:
      return MonitoringLocationChangeAddressTimeZone.America_New_York
    case LocationAddressTimeZone.America_Chicago:
      return MonitoringLocationChangeAddressTimeZone.America_Chicago
    case LocationAddressTimeZone.America_Denver:
      return MonitoringLocationChangeAddressTimeZone.America_Denver
    case LocationAddressTimeZone.America_Phoenix:
      return MonitoringLocationChangeAddressTimeZone.America_Phoenix
    case LocationAddressTimeZone.America_Los_Angeles:
      return MonitoringLocationChangeAddressTimeZone.America_Los_Angeles
    case LocationAddressTimeZone.America_Anchorage:
      return MonitoringLocationChangeAddressTimeZone.America_Anchorage
    case LocationAddressTimeZone.Pacific_Honolulu:
      return MonitoringLocationChangeAddressTimeZone.Pacific_Honolulu
    case LocationAddressTimeZone.Europe_London:
      return MonitoringLocationChangeAddressTimeZone.Europe_London
    case LocationAddressTimeZone.America_Puerto_Rico:
      return MonitoringLocationChangeAddressTimeZone.America_Puerto_Rico
    case LocationAddressTimeZone.Pacific_Guam:
      return MonitoringLocationChangeAddressTimeZone.Pacific_Guam
    case LocationAddressTimeZone.Unresolved:
    case LocationAddressTimeZone.Unknown:
    default:
      return MonitoringLocationChangeAddressTimeZone.Unknown
  }
}

export function toLocationChangeWithAddress(locationChange: IncompleteLocationChange | undefined): LocationChangeWithAddress {
  if (!locationChange) {
    throw new Error('Current location change must be set before rendering the address form')
  }
  if (!locationChange.address) {
    throw new Error('Current location change does not have an address associated with it')
  }
  return locationChange as LocationChangeWithAddress
}

export function getUserUserFriendlyMessage(issueOrCorrection: AddressValidationIssue): IssueMessageForField | undefined {
  const { type: issueType } = issueOrCorrection
  if (ADDRESS_ISSUE_MAPPINGS[issueType]) {
    return ADDRESS_ISSUE_MAPPINGS[issueType]
  }
  return undefined
}
